import { useCallback, useEffect, useMemo, useState } from "react"
import { Virtuoso } from "react-virtuoso"
import { numberFormat } from "shared/util"
import { Box, Stack, Slide, Divider, Typography } from "@mui/material"

import { GridColumnDef, GridColumnDefMap, GridFilter, GridFilterRange } from "../../types"
import { isFilterd } from "../../util"
import { RangeFilter, buildFilterLabel } from "./range"

type HistgramFilterPros<T> = {
	field: string
	fieldDef: GridColumnDef<T>
	columnsDef: GridColumnDefMap<T>
	dataList: T[]
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	values: any[] | null
	filter: GridFilter | undefined
	filters: GridFilter[]
	setFilters: (filters: GridFilter[]) => void
}
export const HistgramFilter = <T,>({
	field,
	fieldDef,
	columnsDef,
	dataList,
	values,
	filter,
	filters,
	setFilters
}: HistgramFilterPros<T>) => {
	const [isDragging, setIsDragging] = useState<boolean>(false)
	const [dragFrom, setDragFrom] = useState<number | null>(null)
	const [dragTo, setDragTo] = useState<number | null>(null)

	const {
		unit,
		histgram
	} = useMemo(
		() => {
			let countMax = 0
			const unit = Math.max(fieldDef.histgram?.unit ?? 1, 1)
			const histgram: {
				value: number
				count: number
				rate?: number
				percent?: number
			}[] = []
			let totalCount = 0
			for(const record of dataList) {
				if (filters.some(filter => filter.field === field ? false : isFilterd(record, filter, columnsDef))) continue
				totalCount++
				const value = fieldDef.value(record) ?? 0
				if (typeof value !== 'number') continue
				const rounded = Math.floor(value / unit)
				const v = histgram.find(h => h.value === rounded)
				if (!v) {
					histgram.push({ value: rounded, count: 1 })
					if (countMax === 0) countMax = 1
				} else {
					v.count = v.count + 1
					if (countMax < v.count) countMax = v.count
				}
			}
			histgram.forEach(row => {
				row.rate = countMax > 0 ? Math.floor((row.count * 100) / countMax) : 0
				row.percent = Math.ceil((row.count * 100 * 10) / totalCount) / 10
			})
			histgram.sort((a, b) => a.value - b.value)
			return { unit, histgram }
		},
		[columnsDef, field, fieldDef, dataList, filters]
	)
	const isCurrent = useCallback(
		(from: number) => values?.some(value => {
			if (typeof value !== 'number') return false
			if ((from * unit) <= value && ((from + 1) * unit) > value) return true
			return false
		}) ?? false,
		[unit, values]
	)
	const dragStart = useCallback(
		(startIndex: number) => {
			setDragFrom(startIndex)
			setDragTo(startIndex)
			setIsDragging(true)
		},
		[setDragFrom, setDragTo, setIsDragging]
	)
	const dragEnter = useCallback(
		(enterIndex: number) => {
			if (!isDragging || dragFrom === null) return
			setDragTo(enterIndex)
		},
		[isDragging, dragFrom, setDragTo]
	)
	const handleMouseUp = useCallback(
		() => {
			if (isDragging && dragFrom !== null && dragTo !== null) {
				const { from, to } = dragFrom < dragTo ?
					{ from: dragFrom, to: dragTo } : { from: dragTo, to: dragFrom }
				const range: GridFilterRange = {
					from: (from > 0) ? (histgram[from].value * unit) : undefined,
					fromEq: true,
					to: (to < (histgram.length-1)) ? (histgram[to+1].value * unit) : undefined,
					toEq: false
				}
				if (filter) {
					if (!range.from && !range.to) {
						// remove filter
						setFilters(filters.filter(f => f.field !== filter.field).slice())
					} else {
						// replace filter
						const newFilter: GridFilter = {
							...filter,
							label: buildFilterLabel(range, fieldDef.suffix),
							range
						}
						setFilters(filters.map(f => f.field === filter.field ? newFilter : f).slice())
					}
				} else if (range.from || range.to) {
					// new filter
					setFilters(filters.concat({
						field,
						label: buildFilterLabel(range, fieldDef.suffix),
						range
					}).slice())
				}
			}
			setIsDragging(false)
		},
		[isDragging, dragFrom, dragTo, setIsDragging, field, fieldDef, filter, filters, setFilters, histgram, unit]
	)
	useEffect(
		() => {
			document.addEventListener('mouseup', handleMouseUp)
			return () => {
				document.removeEventListener('mouseup', handleMouseUp)
			}
		},
		[handleMouseUp]
	)
	return (
		<RangeFilter
			field={field}
			fieldDef={fieldDef}
			filter={filter}
			filters={filters}
			setFilters={setFilters}
		>
			<Divider/>
			<Stack flexGrow={1} overflow='auto' boxSizing="border-box" width='100%'>
				<Virtuoso
					data={histgram}
					itemContent={(i, row) => (
						<Box
							key={`histgram-${row.value}`}
							padding={0.5}
							onMouseDown={(ev) => {
								ev.stopPropagation()
								dragStart(i)
								ev.preventDefault()
							}}
							onMouseEnter={() => dragEnter(i)}
							sx={{
								"transition": "background 0.05s",
								"bgcolor": isDragging && dragFrom !== null && dragTo !== null && (
									dragFrom > dragTo ? (dragFrom >= i && dragTo <= i) : (dragTo >= i && dragFrom <= i)
								) ? "rgba(0, 0, 0, 0.04)" : undefined,
								'&:hover': {
									bgcolor: "rgba(0, 0, 0, 0.04)"
								},
								"cursor": "pointer"
							}}
						>
							<Box padding={0.5} position='relative' boxSizing='border-box' width="100%" overflow="hidden">
								<Stack direction="row" alignItems="center" position='relative' spacing={1} zIndex={1}>
									<Typography variant="body2" flexGrow={1}>
										{`${numberFormat(row.value * unit)} ${fieldDef.suffix ?? ''} ～`}
									</Typography>
									<Typography variant="caption" whiteSpace="nowrap">{numberFormat(row.count)}件 ({row.percent}%)</Typography>
								</Stack>
								<Slide direction="left" in>
									<div
										key={`histgram-bar-${row.value}`}
										style={{
											position: 'absolute',
											right: 0,
											top: 0, bottom: 0,
											width: `${row.rate}%`,
											background: isCurrent(row.value) ? 'rgb(225,225,245)' : 'rgb(235,235,235)',
											borderRadius: '4px 0 0 4px',
											transition: 'width 0.3s, background 0.3s',
											zIndex: 0
										}}
									/>
								</Slide>
							</Box>
						</Box>
					)}
				/>
			</Stack>
		</RangeFilter>
	)
}